import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  PlusOutlined,
  DeleteOutlined,
  HolderOutlined,
} from '@ant-design/icons';
import {
  Button,
  Input,
  Select,
  Switch,
  InputNumber,
  DatePicker,
  message,
} from 'antd';
import { Draggable } from 'react-beautiful-dnd';
import { useSelector } from 'react-redux';
import StrictModeDroppable from './StrictModeDroppable';
import { uploadFile } from '../../../../../../../helper/uploadFile';
import dayjs from 'dayjs';
import { createOptionEntry, updateOptionTitle } from '../../utils/optionUtils';

const { TextArea } = Input;
const { Option } = Select;

/**
 * Renders a single component editor card within a screen or form.
 */
export default function FlowComponentEditor({
  component,
  componentIndex,
  screenId,
  parentFormIndex = null,
  dragHandleProps,
  onUpdate,
  onDelete,
  isFormChild = false,
  formUtils = {},
  optInUtils = {},
  brandId,
  flowId,
}) {
  if (!component) return null;

  const {
    handleUpdateFormComponent,
    handleDeleteFormComponent,
    createFormDroppableId,
    createFormChildDraggableId,
  } = formUtils;

  const updateComponent = (updates) => {
    if (typeof onUpdate === 'function') {
      onUpdate(updates);
    }
  };

  const deleteComponent = () => {
    if (typeof onDelete === 'function') {
      onDelete();
    }
  };

  const handleChange = (field, value) => {
    updateComponent({ [field]: value });
  };

  const isFooter = component.type === 'Footer';
  const isForm = component.type === 'Form';
  const componentOptions = Array.isArray(component['data-source'])
    ? component['data-source']
    : [];
  const imageFileInputRef = useRef(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  const triggerImagePicker = useCallback(() => {
    if (!flowId) {
      message.warning('Please save the flow before uploading an image.');
      return;
    }
    imageFileInputRef.current?.click();
  }, [flowId]);

  const validateImageFile = (file) => {
    if (!file) return false;

    const allowedTypes = ['png', 'jpg', 'jpeg'];
    const extension = file.name?.split('.').pop()?.toLowerCase();
    if (!allowedTypes.includes(extension)) {
      message.error('Only PNG or JPG images are supported.');
      return false;
    }

    const maxSizeInBytes = 300 * 1024; // 300KB
    if (file.size > maxSizeInBytes) {
      message.error('Please choose an image under 300KB.');
      return false;
    }

    return true;
  };

  const getImageSource = (base64Data) => {
    if (!base64Data) return '';
    // If it's already a data URL, return as is
    if (base64Data.startsWith('data:')) return base64Data;
    // Otherwise, assume it's raw base64 and add the prefix
    return `data:image/jpeg;base64,${base64Data}`;
  };

  const performImageUpload = (file) => {
    if (!validateImageFile(file)) return;

    return new Promise((resolve) => {
      const reader = new FileReader();
      
      reader.onload = (event) => {
        const dataUrl = event.target.result;
        // Extract just the base64 part (remove the data:image/...;base64, prefix)
        const base64Data = dataUrl.split(',')[1];
        handleChange('src', base64Data);
        message.success('Image processed successfully.');
        resolve();
      };
      
      reader.onerror = (error) => {
        console.error('Error reading file:', error);
        message.error('Failed to process the image. Please try again.');
        resolve();
      };
      
      // Read the file as a data URL (base64)
      reader.readAsDataURL(file);
    });
  };

  const handleImageUpload = async (event) => {
    const file = event?.target?.files?.[0];
    if (event?.target) {
      event.target.value = '';
    }
    if (!file) return;

    await performImageUpload(file);
  };

  const handleImageDrop = async (event) => {
    event.preventDefault();
    const file = event.dataTransfer?.files?.[0];
    if (!file) return;
    await performImageUpload(file);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
  };

  const handleRemoveImage = () => {
    handleChange('src', '');
  };

  const DATE_FORMAT = 'YYYY-MM-DD';
  const parseDateValue = (value) => {
    if (!value) return null;
    const parsed = dayjs(value, DATE_FORMAT, true);
    return parsed.isValid() ? parsed : null;
  };

  const minDateValue = parseDateValue(component['min-date']);
  const maxDateValue = parseDateValue(component['max-date']);

  const handleMinDateChange = (date) => {
    if (date && maxDateValue && date.isAfter(maxDateValue, 'day')) {
      message.error('Minimum date cannot be after maximum date');
      return;
    }
    handleChange('min-date', date ? date.format(DATE_FORMAT) : '');
  };

  const handleMaxDateChange = (date) => {
    if (date && minDateValue && date.isBefore(minDateValue, 'day')) {
      message.error('Maximum date cannot be before minimum date');
      return;
    }
    handleChange('max-date', date ? date.format(DATE_FORMAT) : '');
  };

  const disableMinDate = (current) => {
    if (!current || !maxDateValue) return false;
    return current.isAfter(maxDateValue, 'day');
  };

  const disableMaxDate = (current) => {
    if (!current || !minDateValue) return false;
    return current.isBefore(minDateValue, 'day');
  };

  useEffect(() => {
    if (isFormChild && component && component['init-value'] !== undefined) {
      if (typeof onUpdate === 'function') {
        onUpdate({ ['init-value']: undefined });
      }
    }
  }, [component, isFormChild, onUpdate]);

  const matchComponentType = (value) => {
    switch (value) {
      case 'TextHeading':
        return 'Text Heading';
      case 'TextSubheading':
        return 'Text Subheading';
      case 'TextCaption':
        return 'Text Caption';
      case 'TextBody':
        return 'Text Body';
      case 'TextInput':
        return 'Text Input';
      case 'TextArea':
        return 'Text Area';
      case 'DatePicker':
        return 'Date Picker';
      case 'CheckboxGroup':
        return 'Checkbox Group';
      case 'RadioButtonsGroup':
        return 'Radio Buttons Group';
      case 'Form':
        return 'Form';
      case 'Dropdown':
        return 'Dropdown';
      case 'OptIn':
        return 'Opt In';
      case 'Footer':
        return 'Footer';
      case 'Image':
        return 'Image';
      default:
        return 'text';
    }
  }
    

  if (isForm) {
    const formChildren = Array.isArray(component.children) ? component.children : [];
    const formDroppableId =
      typeof createFormDroppableId === 'function'
        ? createFormDroppableId(screenId, componentIndex)
        : '';

    return (
      <div className="space-y-4">
        <StrictModeDroppable droppableId={formDroppableId} type="FORM_COMPONENT">
          {(providedForm) => (
            <div ref={providedForm.innerRef} {...providedForm.droppableProps} className="space-y-3">
              {formChildren.length === 0 && (
                <div className="rounded-lg border border-dashed border-gray-200 bg-blue-50 px-4 py-6 text-center text-xs text-gray-500">
                  No content blocks yet. Use "Add content block" below to insert one.
                </div>
              )}

              {formChildren.map((child, childIndex) => {
                if (!child || typeof child !== 'object' || !child.type) return null;

                const draggableId =
                  typeof createFormChildDraggableId === 'function'
                    ? createFormChildDraggableId(screenId, componentIndex, childIndex)
                    : `${screenId}-${componentIndex}-${childIndex}`;

                return (
                  <Draggable
                    key={draggableId}
                    draggableId={draggableId}
                    index={childIndex}
                    isDragDisabled={child.type === 'Footer'}
                  >
                    {(formProvided) => (
                      <div ref={formProvided.innerRef} {...formProvided.draggableProps}>
                        <FlowComponentEditor
                          component={child}
                          componentIndex={childIndex}
                          screenId={screenId}
                          parentFormIndex={componentIndex}
                          dragHandleProps={formProvided.dragHandleProps}
                          onUpdate={(updates) =>
                            handleUpdateFormComponent?.(
                              screenId,
                              componentIndex,
                              childIndex,
                              updates
                            )
                          }
                          onDelete={() =>
                            handleDeleteFormComponent?.(screenId, componentIndex, childIndex)
                          }
                          formUtils={formUtils}
                          optInUtils={optInUtils}
                          isFormChild
                          brandId={brandId}
                          flowId={flowId}
                        />
                      </div>
                    )}
                  </Draggable>
                );
              })}

              {providedForm.placeholder}
            </div>
          )}
        </StrictModeDroppable>
      </div>
    );
  }

  const containerClassName = [
    'mb-5 overflow-hidden rounded-xl border border-blue-200 shadow-sm transition-all hover:shadow',
    isFormChild ? 'bg-blue-50' : 'bg-white',
  ].join(' ');

  const headerDragHandleProps = isFooter ? {} : dragHandleProps || {};

  let content = null;
  const currentAction = component['on-click-action'] || null;
  const currentActionName = currentAction?.name || 'none';
  const currentActionUrl = currentAction?.url || '';

  content = (
    <>
      {['TextHeading', 'TextSubheading', 'TextCaption', 'TextBody'].includes(component.type) && (
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Text</label>
            <TextArea
              value={component.text}
              onChange={(e) => handleChange('text', e.target.value)}
              placeholder="Enter text"
              maxLength={
                component.type === 'TextHeading'
                  ? 80
                  : component.type === 'TextBody'
                  ? 4096
                  : 500
              }
              rows={component.type === 'TextBody' ? 4 : 2}
              showCount
            />
            <span className="mt-1 block text-xs text-gray-500">
              Max:{' '}
              {component.type === 'TextHeading'
                ? '80'
                : component.type === 'TextBody'
                ? '4096'
                : '500'}{' '}
              characters
            </span>
          </div>
        </div>
      )}

      {component.type === 'Image' && (
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Image
            </label>
            {component.src ? (
              <div className="space-y-3">
                <div className="relative overflow-hidden rounded-lg border border-blue-200 bg-white">
                  <img
                    src={getImageSource(component.src)}
                    alt="Flow component preview"
                    className="w-full rounded-lg object-contain"
                    style={{ maxHeight: 320 }}
                    onError={(e) => {
                      console.error('Error loading image:', component.src);
                      e.target.alt = 'Failed to load image';
                    }}
                  />
                  {isUploadingImage && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white/70 text-sm font-medium text-gray-700">
                      Uploading…
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="primary"
                    onClick={triggerImagePicker}
                    loading={isUploadingImage}
                    disabled={isUploadingImage}
                  >
                    {isUploadingImage ? 'Uploading' : 'Replace image'}
                  </Button>
                  <Button onClick={handleRemoveImage} disabled={isUploadingImage}>
                    Remove
                  </Button>
                </div>
              </div>
            ) : (
              <div
                className="flex cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-blue-300 bg-blue-50 px-4 py-10 text-center transition hover:border-blue-400 hover:bg-blue-100 focus-within:border-blue-400"
                onClick={triggerImagePicker}
                onDragOver={handleDragOver}
                onDrop={handleImageDrop}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    triggerImagePicker();
                  }
                }}
              >
                <PlusOutlined className="text-xl text-[#1daa61]" />
                <p className="mt-2 text-sm font-medium text-gray-700">
                  Drag & drop or click to upload
                </p>
                <p className="text-xs text-gray-500">
                  PNG or JPG up to 300KB
                </p>
                {isUploadingImage && (
                  <p className="mt-2 text-xs font-medium text-[#1daa61]">
                    Uploading…
                  </p>
                )}
              </div>
            )}
            <input
              ref={imageFileInputRef}
              type="file"
              accept="image/png,image/jpeg"
              className="hidden"
              onChange={handleImageUpload}
            />
            <p className="mt-2 text-xs text-gray-500">
              PNG or JPG up to 300KB. Square images work best for WhatsApp flow previews.
            </p>
          </div>
        </div>
      )}

        {component.type === 'TextInput' && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Label</label>
              <Input
                value={component.label}
                onChange={(e) => handleChange('label', e.target.value)}
                placeholder="Enter label"
                maxLength={20}
                showCount
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Input Type</label>
              <Select
                value={component['input-type']}
                onChange={(value) => handleChange('input-type', value)}
                className="w-full"
              >
                <Option value="text">Text</Option>
                {/* <Option value="number">Number</Option> */}
                <Option value="email">Email</Option>
                <Option value="password">Password</Option>
                <Option value="passcode">Passcode</Option>
                <Option value="phone">Phone</Option>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Min Characters
                </label>
                <InputNumber
                  value={component['min-chars']}
                  onChange={(value) => handleChange('min-chars', value)}
                  min={0}
                  max={component['max-chars'] || 35}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Characters
                </label>
                <InputNumber
                  value={component['max-chars']}
                  onChange={(value) => handleChange('max-chars', value)}
                  min={component['min-chars'] || 0}
                  max={4096}
                  className="w-full"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Helper Text</label>
              <Input
                value={component['helper-text']}
                onChange={(e) => handleChange('helper-text', e.target.value)}
                placeholder="Optional helper text"
                maxLength={80}
                showCount
              />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Required</span>
              <Switch
                checked={component.required}
                onChange={(checked) => handleChange('required', checked)}
              />
            </div>
            {currentActionName === 'open_url' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">URL</label>
                <Input
                  value={currentActionUrl}
                  onChange={(e) =>
                    handleChange('on-click-action', {
                      name: 'open_url',
                      url: e.target.value,
                    })
                  }
                  placeholder="https://example.com"
                />
              </div>
            )}
          </div>
        )}

        {component.type === 'TextArea' && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Label</label>
              <Input
                value={component.label}
                onChange={(e) => handleChange('label', e.target.value)}
                placeholder="Enter label"
                maxLength={20}
                showCount
              />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Required</span>
              <Switch
                checked={component.required}
                onChange={(checked) => handleChange('required', checked)}
              />
            </div>
            {currentActionName === 'open_url' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">URL</label>
                <Input
                  value={currentActionUrl}
                  onChange={(e) =>
                    handleChange('on-click-action', {
                      name: 'open_url',
                      url: e.target.value,
                    })
                  }
                  placeholder="https://example.com"
                />
              </div>
            )}
          </div>
        )}

        {component.type === 'DatePicker' && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Label</label>
              <Input
                value={component.label}
                onChange={(e) => handleChange('label', e.target.value)}
                placeholder="Enter label"
                maxLength={20}
                showCount
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Minimum Date</label>
              <DatePicker
                value={minDateValue}
                onChange={handleMinDateChange}
                format={DATE_FORMAT}
                allowClear
                className="w-full"
                placeholder="Select minimum date"
                disabledDate={disableMinDate}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Maximum Date</label>
              <DatePicker
                value={maxDateValue}
                onChange={handleMaxDateChange}
                format={DATE_FORMAT}
                allowClear
                className="w-full"
                placeholder="Select maximum date"
                disabledDate={disableMaxDate}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Helper Text</label>
              <Input
                value={component['helper-text']}
                onChange={(e) => handleChange('helper-text', e.target.value)}
                placeholder="Optional helper text"
                maxLength={80}
                showCount
              />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Required</span>
              <Switch
                checked={component.required}
                onChange={(checked) => handleChange('required', checked)}
              />
            </div>
          </div>
        )}

        {component.type === 'RadioButtonsGroup' && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Question</label>
              <Input
                value={component.label}
                onChange={(e) => handleChange('label', e.target.value)}
                placeholder="Enter question"
                maxLength={30}
                showCount
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Options</label>
              <div className='flex flex-col gap-1'>
                {componentOptions.map((option, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Input
                      value={option.title}
                      onChange={(e) => {
                        const next = [...componentOptions];
                        next[idx] = updateOptionTitle(
                          next[idx],
                          e.target.value,
                          `Option ${idx + 1}`
                        );
                        handleChange('data-source', next);
                      }}
                      placeholder={`Option ${idx + 1}`}
                      maxLength={30}
                      showCount
                    />
                    <Button
                      type="text"
                      className="flex items-center justify-center text-red-500 hover:text-red-600"
                      icon={<DeleteOutlined />}
                      onClick={() => {
                        const next = componentOptions.filter((_, i) => i !== idx);
                        if (next.length > 0) {
                          handleChange('data-source', next);
                        } else {
                          message.error('At least one option is required');
                        }
                      }}
                      disabled={componentOptions.length <= 1}
                    />
                  </div>
                ))}          
              </div>
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={() => {
                  if (componentOptions.length >= 20) {
                    message.error('Maximum 20 options allowed');
                    return;
                  }
                  const next = [
                    ...componentOptions,
                    createOptionEntry(`Option ${componentOptions.length + 1}`),
                  ];
                  handleChange('data-source', next);
                }}
                className="w-full mt-2"
              >
                Add option
              </Button>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Required</span>
              <Switch
                checked={component.required}
                onChange={(checked) => handleChange('required', checked)}
              />
            </div>
          </div>
        )}

        {component.type === 'CheckboxGroup' && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Question</label>
              <Input
                value={component.label}
                onChange={(e) => handleChange('label', e.target.value)}
                placeholder="Enter question"
                maxLength={30}
                showCount
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Options</label>
              {componentOptions.map((option, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Input
                    value={option.title}
                    onChange={(e) => {
                      const next = [...componentOptions];
                      next[idx] = updateOptionTitle(
                        next[idx],
                        e.target.value,
                        `Option ${idx + 1}`
                      );
                      handleChange('data-source', next);
                    }}
                    placeholder={`Option ${idx + 1}`}
                    maxLength={30}
                    className='mb-1'
                    showCount
                  />
                  <Button
                    type="text"
                    className="flex items-center justify-center text-red-500 hover:text-red-600"
                    icon={<DeleteOutlined />}
                    onClick={() => {
                      const next = componentOptions.filter((_, i) => i !== idx);
                      if (next.length > 0) {
                        handleChange('data-source', next);
                      } else {
                        message.error('At least one option is required');
                      }
                    }}
                    disabled={componentOptions.length <= 1}
                  />
                </div>
              ))}
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={() => {
                  if (componentOptions.length >= 20) {
                    message.error('Maximum 20 options allowed');
                    return;
                  }
                  const next = [
                    ...componentOptions,
                    createOptionEntry(`Option ${componentOptions.length + 1}`),
                  ];
                  handleChange('data-source', next);
                }}
                className="w-full"
              >
                Add option
              </Button>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Required</span>
              <Switch
                checked={component.required}
                onChange={(checked) => handleChange('required', checked)}
              />
            </div>
          </div>
        )}

        {component.type === 'Dropdown' && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Label</label>
              <Input
                value={component.label}
                onChange={(e) => handleChange('label', e.target.value)}
                placeholder="Enter label"
                maxLength={30}
                showCount
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Options</label>
              {componentOptions.map((option, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Input
                    value={option.title}
                    onChange={(e) => {
                      const next = [...componentOptions];
                      next[idx] = updateOptionTitle(
                        next[idx],
                        e.target.value,
                        `Option ${idx + 1}`
                      );
                      handleChange('data-source', next);
                    }}
                    placeholder={`Option ${idx + 1}`}
                    maxLength={30}
                    className='mb-1'
                    showCount
                  />
                  <Button
                    type="text"
                    className="flex items-center justify-center text-red-500 hover:text-red-600"
                    icon={<DeleteOutlined />}
                    onClick={() => {
                      const next = componentOptions.filter((_, i) => i !== idx);
                      if (next.length > 0) {
                        handleChange('data-source', next);
                      } else {
                        message.error('At least one option is required');
                      }
                    }}
                    disabled={componentOptions.length <= 1}
                  />
                </div>
              ))}
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={() => {
                  if (componentOptions.length >= 100) {
                    message.error('Maximum 100 options allowed');
                    return;
                  }
                  const next = [
                    ...componentOptions,
                    createOptionEntry(`Option ${componentOptions.length + 1}`),
                  ];
                  handleChange('data-source', next);
                }}
                className="w-full"
              >
                Add option
              </Button>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Required</span>
              <Switch
                checked={component.required}
                onChange={(checked) => handleChange('required', checked)}
              />
            </div>
          </div>
        )}

        {component.type === 'OptIn' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 pt-[12px]">Label</label>
              <TextArea
                value={component.label}
                onChange={(e) => handleChange('label', e.target.value)}
                placeholder="I agree to the terms."
                maxLength={280}
                showCount
                autoSize={{ minRows: 2, maxRows: 4 }}
              />
            </div>
            <div className="flex items-center justify-between mt-[16px]">
              <span className="text-sm font-medium text-gray-700">Required</span>
              <Switch
                checked={component.required}
                onChange={(checked) => handleChange('required', checked)}
              />
            </div>
            {!isFormChild ? (
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">
                  Default to selected
                </span>
                <Switch
                  checked={Boolean(component['init-value'])}
                  onChange={(checked) =>
                    handleChange('init-value', checked ? true : undefined)
                  }
                />
              </div>
            ) : (
              <p className="text-xs text-gray-500">
                Default state is managed by the enclosing form.
              </p>
            )}

            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700">
                Read more link <span className="text-xs text-gray-400">· Optional</span>
              </p>
              {(() => {
                const readMoreScreenId =
                  currentActionName === 'navigate'
                    ? currentAction?.next?.name
                    : null;
                const readMoreScreen =
                  readMoreScreenId && typeof optInUtils?.getReadMoreScreen === 'function'
                    ? optInUtils.getReadMoreScreen(readMoreScreenId)
                    : null;

                if (readMoreScreen) {
                  return (
                    <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-gray-800">"Read more" screen</p>
                          <p className="text-xs text-gray-500">
                            {readMoreScreen.title || readMoreScreen.id}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <button
                            type="button"
                            className="text-sm font-semibold text-blue-600 hover:text-blue-700"
                            onClick={() =>
                              optInUtils?.editReadMoreScreen?.(readMoreScreenId)
                            }
                          >
                            Edit content
                          </button>
                          <button
                            type="button"
                            className="text-sm font-semibold text-red-500 hover:text-red-600"
                            onClick={() =>
                              optInUtils?.removeReadMoreScreen?.({
                                readMoreScreenId,
                                parentScreenId: screenId,
                              })
                            }
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                }

                return (
                  <div className="space-y-3">
                    <button
                      type="button"
                      className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-blue-200 bg-blue-50 px-3 py-2 text-sm font-semibold text-gray-700 transition hover:border-blue-300 hover:bg-white"
                      onClick={() =>
                        optInUtils?.addReadMoreScreen?.({
                          screenId,
                          componentIndex,
                          formIndex: parentFormIndex,
                        })
                      }
                      disabled={
                        typeof optInUtils?.addReadMoreScreen !== 'function'
                      }
                    >
                      <PlusOutlined className="text-xs" />
                      Add "Read more" screen
                    </button>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Alternate link action
                      </label>
                      <Select
                        value={
                          currentActionName === 'open_url' ? 'open_url' : 'none'
                        }
                        className="w-full"
                        onChange={(value) => {
                          if (value === 'open_url') {
                            handleChange('on-click-action', {
                              name: 'open_url',
                              url: currentActionUrl || 'https://',
                            });
                          } else {
                            handleChange('on-click-action', null);
                          }
                        }}
                        options={[
                          { value: 'none', label: 'No link' },
                          { value: 'open_url', label: 'Open URL' },
                        ]}
                      />
                    </div>
                    {currentActionName === 'open_url' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          URL
                        </label>
                        <Input
                          value={currentActionUrl}
                          onChange={(e) =>
                            handleChange('on-click-action', {
                              name: 'open_url',
                              url: e.target.value,
                            })
                          }
                          placeholder="https://example.com"
                        />
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {component.type === 'Footer' && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Button Text</label>
              <Input
                value={component.label}
                onChange={(e) => handleChange('label', e.target.value)}
                placeholder="Enter button text"
                maxLength={20}
                showCount
              />
            </div>
            <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-gray-700">
              Footer automatically navigates to the next screen based on screen order.
            </div>
          </div>
        )}
      </>
    );

  const contentWrapperClassName = ['space-y-4', 'bg-white', 'px-4', 'py-4'];
  if (isFormChild) {
    contentWrapperClassName[1] = 'bg-blue-50';
  }

  return (
    <div className={containerClassName}>
      <div className="flex items-center justify-between border-b border-blue-200 bg-blue-100 px-4 py-3">
        <div className="flex items-center gap-3">
          {isFooter ? (
            // <span className="flex h-8 w-8 items-center justify-center" />
            <></>
          ) : (
            <span
              className="flex h-8 w-8 items-center justify-center rounded-full border border-blue-200 bg-white text-lg text-gray-600"
              {...headerDragHandleProps}
            >
              <HolderOutlined />
            </span>
          )}
          <span className="text-sm font-semibold text-gray-700">{matchComponentType(component.type)}</span>
        </div>
        {!isFooter && (
          <Button
            type="text"
            className="flex items-center justify-center text-red-500 hover:text-red-600"
            icon={<DeleteOutlined />}
            onClick={deleteComponent}
          />
        )}
      </div>

      <div className={contentWrapperClassName.join(' ')}>{content}</div>
    </div>
  );
}
